import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Walk a formatted numeric string and flag its "anchor" characters: the digits,
 * the decimal point and the minus sign. These keep their relative order when the
 * value is re-formatted, so the number of them to the left of the caret is a
 * stable cursor position across a re-format.
 *
 * Thousands-separator commas and a currency prefix ("$") are NOT anchors — they
 * are inserted/removed as the integer part grows, so they are skipped. A leading
 * integer zero is skipped too: `formatInputValue` strips it, so anchoring on one
 * would push the caret a slot to the right (e.g. typing "0" in front of "1,234").
 * Counting the decimal point is what keeps the caret on the correct side of a "."
 * the user just typed, so "0" then "." then "5" yields "0.5" rather than "5.".
 *
 * @param {string} value - The formatted string to classify.
 * @return {boolean[]} One flag per character: true where it is an anchor.
 */
const anchorFlags = (value: string): boolean[] => {
  const flags: boolean[] = [];
  let seenNonZeroDigit = false;
  let seenDot = false;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === '-') {
      flags.push(true);
    } else if (ch === '.') {
      seenDot = true;
      flags.push(true);
    } else if (ch >= '1' && ch <= '9') {
      seenNonZeroDigit = true;
      flags.push(true);
    } else if (ch === '0') {
      // A leading integer zero is stripped on re-format; don't anchor on it.
      // Once a non-zero digit or the decimal point has appeared, zeros survive.
      flags.push(seenNonZeroDigit || seenDot);
    } else {
      // Commas, "$" and any other decoration move on re-format: not anchors.
      flags.push(false);
    }
  }
  return flags;
};

/**
 * Count the anchor characters (digits, ".", "-") in a string, skipping commas,
 * "$" and stripped leading zeros. See {@link anchorFlags}.
 *
 * @param {string} value - The string to scan (usually the text left of the caret).
 * @return {number} The number of anchor characters in `value`.
 */
export const countAnchorChars = (value: string): number =>
  anchorFlags(value).reduce(
    (count, isAnchor) => (isAnchor ? count + 1 : count),
    0
  );

/**
 * Caret index that sits just after `anchorCount` anchor characters in a formatted
 * string — the inverse of {@link countAnchorChars}. A count of 0 or less places
 * the caret just before the first anchor character (e.g. after a leading "$" or
 * "-"), or at the end when the string has none (e.g. a lone, stripped "0").
 *
 * @param {string} formatted - The formatted string to position within.
 * @param {number} anchorCount - Anchor characters that should sit left of the caret.
 * @return {number} The caret index just after that many anchor characters.
 */
export const caretIndexForAnchorCount = (
  formatted: string,
  anchorCount: number
): number => {
  const flags = anchorFlags(formatted);
  if (anchorCount <= 0) {
    const first = flags.indexOf(true);
    return first === -1 ? formatted.length : first;
  }

  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (flags[i]) {
      seen += 1;
      if (seen === anchorCount) return i + 1;
    }
  }
  return formatted.length;
};

interface PreservedCaret {
  /** Attach to the underlying `<input>` (Carbon `TextInput` forwards the ref). */
  inputRef: React.RefObject<HTMLInputElement>;
  /**
   * Call in the input's `onChange` BEFORE updating state, to record where the
   * caret is (anchored on significant characters) so it can be restored after
   * re-formatting.
   */
  rememberCaret: () => void;
}

/**
 * Preserve the text caret across controlled re-formatting of a numeric input.
 *
 * A controlled input that reformats its value on every render (re-inserting
 * commas, a "$" or ".00") writes the formatted string back to the DOM input,
 * which resets the caret to the end — jarring when editing mid-number, e.g.
 * deleting a digit next to a thousands-separator comma, or typing a decimal
 * point. This hook records the caret's anchor offset (significant characters:
 * digits, "." and "-") on each edit and restores it after the reformatted value
 * is painted. It only acts on user edits, so external value changes never move
 * the caret.
 *
 * @param {unknown} valueDep - A value that changes on every edit (usually the
 *   displayed string); the caret is restored after it changes.
 * @return {PreservedCaret} `inputRef` to attach to the `<input>`, and
 *   `rememberCaret` to call in `onChange` before updating state.
 */
export const useCaretPreservation = (valueDep: unknown): PreservedCaret => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingAnchorRef = useRef<number | null>(null);
  const clearFrameRef = useRef<number | null>(null);

  const rememberCaret = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    pendingAnchorRef.current = countAnchorChars(
      input.value.slice(0, input.selectionStart ?? input.value.length)
    );
    // If this edit leaves the displayed value unchanged (e.g. a stray letter, or
    // a comma that gets stripped), no re-render follows and the layout effect
    // never consumes this anchor. Drop it on the next frame so a later, unrelated
    // value change — the parent updating `value` while the field is focused —
    // can't apply a stale caret position.
    if (clearFrameRef.current !== null) {
      cancelAnimationFrame(clearFrameRef.current);
    }
    clearFrameRef.current = requestAnimationFrame(() => {
      clearFrameRef.current = null;
      pendingAnchorRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    const anchored = pendingAnchorRef.current;
    pendingAnchorRef.current = null;
    if (clearFrameRef.current !== null) {
      cancelAnimationFrame(clearFrameRef.current);
      clearFrameRef.current = null;
    }
    if (anchored === null) return;

    const input = inputRef.current;
    if (!input || document.activeElement !== input) return;

    const position = caretIndexForAnchorCount(input.value, anchored);
    input.setSelectionRange(position, position);
  }, [valueDep]);

  useEffect(
    () => () => {
      if (clearFrameRef.current !== null) {
        cancelAnimationFrame(clearFrameRef.current);
      }
    },
    []
  );

  return { inputRef, rememberCaret };
};
